import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CDN_TYPE_CMS, CDN_TYPE_GAME, imageUrlCDN } from 'utils';

import {
  SiteConfigManager,
  useApplicationState,
  useCasinoGamesState,
  useCasinoNewState,
} from '@atlas/core';
import { Icon } from '@atlas/ui';

import useTranslate from 'src/common/I18n/Interpreter';
import MultiFilter from 'src/components/Pages/Casino/Filter/MultiFilter/MultiFilter';
import MultiFilterSelectedList from 'src/components/Pages/Casino/Filter/MultiFilter/MultiFilterSelectedList';

import GameList from './GameList/GameList';
import { GAME_PER_PAGE } from './constants';
import InfiniteScroller from './InfiniteScroller';
import LoadMoreButton from './LoadMoreButton';

const noop = () => {};

const ALL_GAMES = 'all_games';

export const CasinoCategoryGames = ({
  activeItem,
  homePageFunctionality,
  scrollParent,
  setActiveItem,
  sliderSettings,
}) => {
  const [isEnableInfinityScroll, setIsEnableInfinityScroll] = useState(false);
  const {
    categories: { data: categories },
  } = useCasinoNewState();
  const t = useTranslate();
  const { currentSlice, error, games, hasNext, loadMore, loaded, loading } = useCasinoGamesState();
  const { authenticated } = useApplicationState();
  const { identifyFavoriteGames } = useCasinoNewState();
  const params = useParams();
  const buttonProperties = SiteConfigManager.getConfig('config.config.buttonProperties');

  const categoryItem = useMemo(() => {
    const category = categories.find(category => category.id === +activeItem);

    return category || {};
  }, [activeItem, categories]);

  const handleLoadMore = useCallback(() => {
    loadMore(currentSlice + 1);
  }, [currentSlice, loadMore]);

  useEffect(() => {
    if (!authenticated || !games.length) return;

    const gameIds = games.map(({ id }) => id);

    identifyFavoriteGames(gameIds);
  }, [games, authenticated, identifyFavoriteGames]);

  useEffect(() => {
    // disable infinity scroll when change category
    setIsEnableInfinityScroll(false);
  }, [params]);

  const handleEnableInfinityScroll = () => {
    handleLoadMore();
    setIsEnableInfinityScroll(true);
  };

  const gameLength = games.length;
  const canRenderLoadMoreButton = useMemo(() => {
    return !isEnableInfinityScroll && !loading && gameLength >= GAME_PER_PAGE;
  }, [gameLength, isEnableInfinityScroll, loading]);

  const renderAllGamesIcon = useMemo(() => {
    const allGamesButton = buttonProperties.find(button => button.type === ALL_GAMES);
    const { icon, type, useDefaultIcon } = allGamesButton;

    if (useDefaultIcon) {
      return <Icon name="games" />;
    }

    return (
      <img alt={type} src={encodeURI(imageUrlCDN(icon, CDN_TYPE_CMS))} />
    )
  }, []);

  return (
    <>
      {homePageFunctionality && (
        <div className="casino-game-list__header">
          <div className="casino-game-list__header-top">
            <div className="casino-category-title">
              {activeItem === ALL_GAMES ? (
                <>
                  {renderAllGamesIcon}
                  <h2>{t(ALL_GAMES)}</h2>
                </>
              ) : (
                <>
                  {categoryItem.iconUrl && (
                    <img
                      alt={categoryItem?.name}
                      src={encodeURI(imageUrlCDN(categoryItem.iconUrl, CDN_TYPE_GAME))}
                    />
                  )}
                  <h2>{categoryItem?.name}</h2>
                </>
              )}
            </div>
            <MultiFilter />
          </div>
          <div className="casino-game-list__header-bottom">
            <MultiFilterSelectedList />
          </div>
        </div>
      )}
      <InfiniteScroller
        getScrollParent={scrollParent}
        hasMore={hasNext && isEnableInfinityScroll && !error}
        isLoading={loading}
        isResolved
        loadMore={isEnableInfinityScroll ? handleLoadMore : noop}
      >
        <GameList
          activeItem={activeItem}
          displayFilterProviders={homePageFunctionality}
          games={games}
          hasLoadMoreButton={canRenderLoadMoreButton}
          isEnableInfinityScroll={isEnableInfinityScroll}
          isLoading={loading}
          isResolved={loaded}
          scrollParent={scrollParent}
          setActiveItem={setActiveItem}
          sliderSettings={sliderSettings}
        />
        <LoadMoreButton handleClick={handleEnableInfinityScroll} isShow={canRenderLoadMoreButton} />
      </InfiniteScroller>
    </>
  );
};
